import styled, { css } from 'styled-components';

import { Box } from '@/components/Box';
import { Flex } from '@/components/Flex';
import { Grid as BaseGrid } from '@/components/Grid';
import { Loader as BaseLoader } from '@/components/Loader';
import { Overlay } from '@/components/Overlay';
import { SectionHeader } from '@/components/SectionHeader';
import { Typography } from '@/components/Typography';
import { responsive } from '@/theme';

export const Wrapper = styled(Box)`
  background-color: ${({ theme }) => theme.colors.lightGrey};
`;

export const Subheader = styled(Typography)`
  text-transform: uppercase;
  text-align: center;
  line-height: unset;

  ${({ theme }) => css`
    padding-top: ${theme.spacing(4)};
    font-weight: ${theme.fontWeights.bold};
    font-family: ${theme.fontFamilies.italianPlate};
    font-size: ${theme.fontSizes.h2Mobile};

    ${theme.media.md.up} {
      font-size: ${theme.fontSizes.h2};
    }
  `}
`;

export const SubTitle = styled(Typography)`
  text-transform: uppercase;
  text-align: center;
  line-height: unset;

  ${({ theme }) => css`
    padding-top: ${theme.spacing(1)};
    font-family: ${theme.fontFamilies.italianPlate};
    font-size: ${theme.fontSizes.h5Mobile};
  `}
`;

export const Grid = styled(BaseGrid)<{
  $isFullWidth?: boolean;
  $isProcessing: boolean;
  $mainMaxWidth?: number;
  $sidebarMaxWidth?: number;
}>`
  position: relative;

  ${({
    $isFullWidth,
    $isProcessing,
    $mainMaxWidth,
    $sidebarMaxWidth,
    theme,
  }) => css`
    ${$isProcessing &&
    css`
      ::before {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        content: ' ';
        background: ${theme.colors.white};
        opacity: 0.5;
        pointer-events: all;
        z-index: 5;
      }
    `}

    ${theme.media.md.up} {
      grid-template-columns: ${$isFullWidth
        ? `minmax(auto, ${$mainMaxWidth ?? 840}px)`
        : `minmax(auto, ${$mainMaxWidth ?? 840}px) ${
            $sidebarMaxWidth ?? 400
          }px`};
      grid-gap: ${theme.spacing(2)};
      justify-content: center;
      padding: ${theme.spacing(0, 2)};
    }
  `}
`;

export const Loader = styled(BaseLoader)`
  position: absolute;
  z-index: 5;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

export const Section = styled(Flex)<{
  $paddingInlineSpacing?: number;
  withBackground: boolean;
}>`
  &&& {
    overflow: hidden;
    flex-direction: column;
    text-align: left;

    ${({ $paddingInlineSpacing, theme, withBackground }) => css`
      border-bottom: 1px solid rgba(0, 0, 0, 0.05);
      margin-bottom: ${theme.spacing(2)};

      & > * {
        ${withBackground &&
        css`
          background-color: ${theme.colors.white};
        `}
        padding: ${theme.spacing(2)};

        ${theme.media.md.up} {
          padding: ${theme.spacing(4)};

          ${$paddingInlineSpacing !== undefined &&
          css`
            padding-inline: ${theme.spacing($paddingInlineSpacing)};
          `}
        }
      }
    `}
  }
`;

export const Container = styled(Box)`
  text-align: center;

  ${({ theme }) => css`
    color: ${theme.colors.secondary};
  `}
`;

export const StepperWrapper = styled(Box)`
  margin: 0 auto;
  padding-block: ${({ theme }) => theme.spacing(2)};

  ${responsive('max-width', '235px', '385px', 'sm')}

  ${({ theme }) => css`
    ${theme.media.md.up} {
      padding-block: ${theme.spacing(4)};
    }
  `}
`;

export const Header = styled(SectionHeader)`
  ${({ theme }) =>
    responsive('padding-block', theme.spacing(2), theme.spacing(2))}
  margin: 0;

  ${({ theme }) => css`
    ${theme.media.md.up} {
      ${({ theme }) =>
        responsive('padding-block', theme.spacing(4), theme.spacing(5))}
    }
  `}
`;

export const ModalHeader = styled.header`
  text-transform: uppercase;
  display: flex;
  justify-content: center;
  align-items: center;

  ${({ theme }) => css`
    margin-top: ${theme.spacing(2)};
    font-size: ${theme.fontSizes.h4};
    font-family: ${theme.fontFamilies.italianPlate};
    font-weight: ${theme.fontWeights.bold};
    color: ${theme.colors.secondary};
  `}
`;

export const ModalBody = styled(Box)`
  font-size: ${({ theme }) => theme.fontSizes.p2};
`;

export const ModalDescription = styled(Box)`
  text-align: center;

  ${({ theme }) => css`
    line-height: ${theme.lineHeights.p1};
    margin-top: ${theme.spacing(1)};
    margin-bottom: ${theme.spacing(2)};
  `}
`;

export const ModalFooter = styled.footer`
  display: flex;
  flex-direction: column;

  ${({ theme }) => css`
    ${theme.media.sm.down} {
      background-color: ${theme.colors.white};
      inset: auto 0 0 0;
    }
  `}
`;

export const ModalFooterWrapper = styled(Flex)`
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const StyledOverlay = styled(Overlay).attrs({
  style: {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
  },
})``;

export const PortalContentWrapper = styled(Box)<{ $showModal: boolean }>`
  position: relative;
  overflow: auto;
  width: 350px;
  isolation: isolate;

  ${({ $showModal, theme }) => css`
    background-color: ${theme.colors.white};
    height: fit-content;
    padding: ${theme.spacing(2)};
    margin: ${theme.spacing(2)};
    opacity: ${$showModal ? 1 : 0};
    pointer-events: ${$showModal ? 'all' : 'none'};
  `};
`;
